import { useForm } from 'react-hook-form';
import {
  always,
  applySpec,
  compose,
  concat,
  cond,
  defaultTo,
  either,
  equals,
  filter,
  find,
  ifElse,
  includes,
  isEmpty,
  isNil,
  length,
  map,
  multiply,
  objOf,
  path,
  pathOr,
  pick,
  prop,
  propEq,
  propOr,
  replace,
  T,
  toString
} from 'ramda';
import useDownloadDocumentsMutation from './useDownloadDocumentsMutation';
import { formatOffer } from '@@viewHelpers/format.jsx';
import { useEditNavigationStore } from '@@app/stores/editNavigationStore';
import { useState } from 'react';
import { useProcess } from '@fasstech/oip-starter-utils/app';
import { offerCodeEnum, signatureStepEnum } from '@@viewEnums';
import {
  formatLegendAndLabel,
  getIsSelected,
  getPropForCode,
  getValue
} from '@@viewHelpers/getPropsGuarantees.js';
import { dateTsToFr } from '@@viewHelpers/date.js';
import { TaskSubscription } from '../../../_common/client/graphql/subscriptions/TaskSubscription.js';
import { useSubscription } from '@apollo/client';
import useExtraSimulationRecapForm from './useExtraSimulationRecapForm.js';
import useSendMailSimulationRecapMutation from './useSendMailSimulationRecapMutation';
import useSaveSimulationRecapCustomerInfoMutation from './useSaveSimulationRecapCustomerInfoMutation';
import useSaveSimulationRecapMutation from './useSaveSimulationRecapMutation';
import { ProjectStatusEnum } from '@fasstech/oip-core/entities/enums';

const useSimulationRecap = ({
  project,
  getMiddleBoolean,
  getMiddleText,
  grantees,
  viewMode,
  usersInformation
}) => {
  const [loading, setLoading] = useState(false);
  const [sendingSignature, setSendingSignature] = useState(false);
  const { onFormSubmit: downloadMutation } = useDownloadDocumentsMutation();
  const { onFormSubmit: sendEmailMutation } =
    useSendMailSimulationRecapMutation();
  const { onFormSubmit: saveCustomerInfoMutation } =
    useSaveSimulationRecapCustomerInfoMutation();
  const [documentsDownloaded, setDocumentsDownloaded] = useState(false);
  const [displayConfirmationModal, setDisplayConfirmationModal] =
    useState(false);
  const [modalConfirmed, setModalConfirmed] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [canSendMail, setCanSendMail] = useState(project.canSendMail);

  const { processHandlers, context } = useProcess();
  const [projectStatus, setProjectStatus] = useState(
    prop('projectStatus', project)
  );

  const form = useForm({
    mode: 'onBlur',
    defaultValues: {
      documentsSelected: {
        PROJECT: true
      },
      offer: compose(formatOffer, prop('offer'))(project),
      commission: prop('commission', project),
      collectionNeeds: prop('collectionNeeds', project),
      customer: prop('customer', project)
    }
  });

  const {
    signatureAdvice,
    isAdviceSignatureSentForSignature,
    isAdviceSignatureError,
    isAdviceSignatureSigned,
    isDutyOfAdviceSkipped,
    data
  } = useExtraSimulationRecapForm({
    getMiddleBoolean,
    projectStatus,
    form
  });

  const $onClickPrev = () => {
    processHandlers.onPrev({
      context: {
        projectId: context?.projectId
      }
    });
  };

  const guarantees = prop('guarantees', project);
  const offerCode = path(['offer', 'code'], project);
  const canContinueToSubscription = prop('canContinueToSubscription', project);
  const subscriptionStatus = path(['customer', 'subscriptionStatus'], project);
  const packs = pathOr([], ['offer', 'packs'])(project);
  const isProjectStatusInProgress = propEq('projectStatus', ProjectStatusEnum.IN_PROGRESS, project);
  const shouldRequireDocumentsDownload = isProjectStatusInProgress;
  const isViewModeCustomer = equals(viewMode, 'CUSTOMER');

  const cantGoNext =
    (signatureAdvice && isAdviceSignatureSentForSignature) ||
    (shouldRequireDocumentsDownload && !documentsDownloaded) ||
    !isDutyOfAdviceSkipped ||
    !canContinueToSubscription;

  const { onFormSubmit } = useSaveSimulationRecapMutation();

  const formValues = form.getValues();

  const onSubmit = () => {
    if (loading || cantGoNext) return;

    if (signatureAdvice && !isAdviceSignatureSigned && !modalConfirmed) {
      return setDisplayConfirmationModal(true);
    }

    if (signatureAdvice && !isAdviceSignatureSigned && modalConfirmed) {
      processHandlers.onKillCronTask(taskData);
      setSendingSignature(true);
      return processHandlers.onRunTask(taskData);
    }
    setLoading(true);

    onFormSubmit(data, (ok, error) => {
      if (ok && !error) {
        processHandlers.onNext({
          context: {
            projectId: context?.projectId
          }
        });
      } else {
        setLoading(false);
      }
    });
  };

  const onModifyEffectDate = () => {
    processHandlers.onPrev({
      context: {
        projectId: context?.projectId,
        action: 'ON_MODIFY_EFFECT_DATE'
      }
    });
  };

  const onEditCustomerGrantees = (customerGranteesTab = 0) => {
    useEditNavigationStore.getState().setCustomerGranteesTab(customerGranteesTab);
    processHandlers.onPrev({
      context: {
        projectId: context?.projectId,
        action: 'ON_EDIT_CUSTOMER_GRANTEES'
      }
    });
  };

  const onEditCompany = () => {
    processHandlers.onPrev({
      context: {
        projectId: context?.projectId,
        action: 'ON_EDIT_COMPANY'
      }
    });
  };

  const onEditDemography = () => {
    processHandlers.onPrev({
      context: {
        projectId: context?.projectId,
        action: 'ON_EDIT_DEMOGRAPHY'
      }
    });
  };

  const calculateTotalPricesByRisk = (offerCode, project) =>
    cond([
      [
        equals(offerCodeEnum.INDIVIDUAL_HEALTH),
        () =>
          applySpec({
            monthlyPrice: pathOr(0, [
              'offer',
              'packs',
              0,
              'pricings',
              0,
              'prices',
              0,
              'defaultPrice'
            ]),
            annualPrice: compose(
              multiply(12),
              pathOr(0, [
                'offer',
                'packs',
                0,
                'pricings',
                0,
                'prices',
                0,
                'price'
              ])
            ),
            pricingLevel: compose(
              replace('IJHOSPI', ''),
              pathOr('', ['offer', 'packs', 0, 'pricings', 0, 'level'])
            )
          })(project)
      ],
      [
        T,
        () =>
          applySpec({
            monthlyPrice: prop('monthlyPrice'),
            annualPrice: prop('annualPrice'),
            monthlyPriceMadelin: prop('monthlyPriceMadelin'),
            annualPriceMadelin: prop('annualPriceMadelin'),
            pricingLevel: prop('pricingLevel')
          })(project)
      ]
    ])(offerCode);

  const numberPricings = compose(
    length,
    filter(
      ({ isPreSelected, supplementaryCover }) =>
        isPreSelected && either(isNil, isEmpty)(supplementaryCover)
    ),
    pathOr([], ['offer', 'packs', 0, 'pricings'])
  )(project);

  const calculatedPrices = calculateTotalPricesByRisk(offerCode, project);
  const {
    TT_CAUSE_CAP,
    OPTION_TT_CAUSE_DOUBL_ACC,
    OPTION_TT_CAUSE_DOUBL_EFFET,
    OPTION_ACC,
    OPTION_ACC_DOUBL_ACC,
    OPTION_ACC_DOUBL_EFFET,
    RCONJ_CAP,
    REDUC_CAP,
    OPTION_INVAL,
    OPTION_INCAP_CAP,
    OPTION_ZERO_SIN,
    OPTION_EXO_COT,
    OPTION_CONFORT_PSY,
    HOSP_ENF_ALLOW_AMOUNT,
    OPTION_FR_GEN,
    INVAL_DEP_CAP
  } = applySpec({
    TT_CAUSE_CAP: find(propEq('code', 'TT_CAUSE_CAP')),
    OPTION_TT_CAUSE_DOUBL_ACC: find(
      propEq('code', 'OPTION_TT_CAUSE_DOUBL_ACC')
    ),
    OPTION_TT_CAUSE_DOUBL_EFFET: find(
      propEq('code', 'OPTION_TT_CAUSE_DOUBL_EFFET')
    ),
    OPTION_ACC: find(propEq('code', 'OPTION_ACC')),
    OPTION_ACC_DOUBL_ACC: find(propEq('code', 'OPTION_ACC_DOUBL_ACC')),
    OPTION_ACC_DOUBL_EFFET: find(propEq('code', 'OPTION_ACC_DOUBL_EFFET')),
    RCONJ_CAP: find(propEq('code', 'RCONJ_CAP')),
    REDUC_CAP: find(propEq('code', 'REDUC_CAP')),
    OPTION_INVAL: find(propEq('code', 'OPTION_INVAL')),
    OPTION_INCAP_CAP: find(propEq('code', 'OPTION_INCAP_CAP')),
    OPTION_ZERO_SIN: find(propEq('code', 'OPTION_ZERO_SIN')),
    OPTION_EXO_COT: find(propEq('code', 'OPTION_EXO_COT')),
    OPTION_CONFORT_PSY: find(propEq('code', 'OPTION_CONFORT_PSY')),
    HOSP_ENF_ALLOW_AMOUNT: find(propEq('code', 'HOSP_ENF_ALLOW_AMOUNT')),
    OPTION_FR_GEN: find(propEq('code', 'OPTION_FR_GEN')),
    INVAL_DEP_CAP: find(propEq('code', 'INVAL_DEP_CAP'))
  })(guarantees || {});

  // PTIADeathPrevTns
  const PTIADeathOptions = [
    {
      displayed:
        getMiddleBoolean(
          'NIVEAUXGARANTIESETTARIFS_GAR_DECES_DECES_PTIA_TTCAUSE_EST_NECESSAIRE'
        ) && getIsSelected('OPTION_TT_CAUSE', guarantees),
      label: getMiddleText(
        'NIVEAUXGARANTIESETTARIFS_GAR_DECES_DECES_PTIA_CAPITAL_LABEL'
      ),
      value: getPropForCode('label', 'TT_CAUSE_CAP')(guarantees),
      classNameLabel: 'font-bold',
      classNameValue: 'text-right font-bold'
    },
    {
      displayed:
        getMiddleBoolean(
          'NIVEAUXGARANTIESETTARIFS_GAR_DECES_DECES_PTIA_ACC_EST_NECESSAIRE'
        ) && getIsSelected('OPTION_ACC', guarantees),
      label: getMiddleText(
        'NIVEAUXGARANTIESETTARIFS_GAR_DECES_DECES_PTIA_CAPITAL_LABEL'
      ),
      value: getPropForCode('label', 'ACC_CAP')(guarantees),
      classNameLabel: 'font-bold',
      classNameValue: 'text-right font-bold'
    },
    {
      displayed:
        getMiddleBoolean(
          'NIVEAUXGARANTIESETTARIFS_GAR_DECES_DECES_PTIA_OPTION_DECES_EST_NECESSAIRE'
        ) &&
        getMiddleBoolean(
          'NIVEAUXGARANTIESETTARIFS_GAR_DECES_DECES_PTIA_OPTION_DECES_DOUBL_ACC_EST_NECESSAIRE'
        ) &&
        ((getMiddleBoolean(
          'NIVEAUXGARANTIESETTARIFS_GAR_DECES_DECES_PTIA_ACC_EST_NECESSAIRE'
        ) &&
          getIsSelected('OPTION_ACC', guarantees) &&
          getIsSelected('OPTION_ACC_DOUBL_ACC', guarantees)) ||
          (getMiddleBoolean(
            'NIVEAUXGARANTIESETTARIFS_GAR_DECES_DECES_PTIA_TTCAUSE_EST_NECESSAIRE'
          ) &&
            getIsSelected('OPTION_TT_CAUSE', guarantees) &&
            getIsSelected('OPTION_TT_CAUSE_DOUBL_ACC', guarantees)) ||
          (getMiddleBoolean(
            'NIVEAUXGARANTIESETTARIFS_GAR_DECES_DECES_PTIA_ACC_EST_NECESSAIRE'
          ) &&
            getIsSelected('OPTION_ACC', guarantees) &&
            getIsSelected('OPTION_ACC_DOUBL_EFFET', guarantees)) ||
          (getMiddleBoolean(
            'NIVEAUXGARANTIESETTARIFS_GAR_DECES_DECES_PTIA_TTCAUSE_EST_NECESSAIRE'
          ) &&
            getIsSelected('OPTION_TT_CAUSE', guarantees) &&
            getIsSelected('OPTION_TT_CAUSE_DOUBL_EFFET', guarantees))),
      label: getMiddleText(
        'NIVEAUXGARANTIESETTARIFS_GAR_DECES_DECES_PTIA_OPTION_DECES_LABEL'
      ),
      classNameLabel: 'font-bold'
    },
    {
      displayed:
        getMiddleBoolean(
          'NIVEAUXGARANTIESETTARIFS_GAR_DECES_DECES_PTIA_OPTION_DECES_DOUBL_ACC_EST_NECESSAIRE'
        ) &&
        getIsSelected('OPTION_ACC_DOUBL_ACC', guarantees) &&
        getIsSelected('OPTION_ACC', guarantees),
      label: getMiddleText(
        'NIVEAUXGARANTIESETTARIFS_GAR_DECES_DECES_PTIA_OPTION_DECES_DOUBL_ACC_LABEL'
      ),
      value: concat(
        toString(propOr(0.0, 'annualPrice', OPTION_ACC_DOUBL_ACC)),
        ' €'
      ),
      classNameValue: 'text-right'
    },
    {
      displayed:
        getMiddleBoolean(
          'NIVEAUXGARANTIESETTARIFS_GAR_DECES_DECES_PTIA_OPTION_DECES_DOUBL_EFFET_EST_NECESSAIRE'
        ) &&
        getIsSelected('OPTION_TT_CAUSE_DOUBL_ACC', guarantees) &&
        getIsSelected('OPTION_TT_CAUSE', guarantees),
      label: getMiddleText(
        'NIVEAUXGARANTIESETTARIFS_GAR_DECES_DECES_PTIA_OPTION_DECES_DOUBL_ACC_LABEL'
      ),
      value: concat(
        toString(propOr(0.0, 'annualPrice', OPTION_TT_CAUSE_DOUBL_ACC)),
        ' €'
      ),
      classNameValue: 'text-right'
    },
    {
      displayed:
        getMiddleBoolean(
          'NIVEAUXGARANTIESETTARIFS_GAR_DECES_DECES_PTIA_OPTION_DECES_DOUBL_EFFET_EST_NECESSAIRE'
        ) &&
        getIsSelected('OPTION_ACC_DOUBL_EFFET', guarantees) &&
        getIsSelected('OPTION_ACC', guarantees),
      label: getMiddleText(
        'NIVEAUXGARANTIESETTARIFS_GAR_DECES_DECES_PTIA_OPTION_DECES_DOUBL_EFFET_LABEL'
      ),
      value: concat(
        toString(propOr(0.0, 'annualPrice', OPTION_ACC_DOUBL_EFFET)),
        ' €'
      ),
      classNameValue: 'text-right'
    },
    {
      displayed:
        getMiddleBoolean(
          'NIVEAUXGARANTIESETTARIFS_GAR_DECES_DECES_PTIA_OPTION_DECES_DOUBL_EFFET_EST_NECESSAIRE'
        ) &&
        getIsSelected('OPTION_TT_CAUSE_DOUBL_EFFET', guarantees) &&
        getIsSelected('OPTION_TT_CAUSE', guarantees),
      label: getMiddleText(
        'NIVEAUXGARANTIESETTARIFS_GAR_DECES_DECES_PTIA_OPTION_DECES_DOUBL_EFFET_LABEL'
      ),
      value: concat(
        toString(propOr(0.0, 'annualPrice', OPTION_TT_CAUSE_DOUBL_EFFET)),
        ' €'
      ),
      classNameValue: 'text-right'
    },
    {
      displayed:
        getIsSelected('OPTION_TT_CAUSE', guarantees) ||
        getIsSelected('OPTION_ACC', guarantees),
      label: getMiddleText(
        'NIVEAUXGARANTIESETTARIFS_GAR_DECES_DECES_PTIA_FISCA_MADELIN_LABEL'
      ),
      value: getIsSelected('TT_CAUSE_MAD', guarantees) ? 'Oui' : 'Non',
      classNameLabel: 'font-bold',
      classNameValue: 'text-right'
    }
  ];

  const PTIADeathGuarantees = ifElse(
    equals(true),
    always({
      title: getMiddleText(
        'NIVEAUXGARANTIESETTARIFS_GAR_DECES_DECES_PTIA_TTCAUSE_LABEL'
      ),
      price: concat(
        toString(propOr(0.0, 'annualPrice', TT_CAUSE_CAP)),
        ' € / an'
      ),
      options: PTIADeathOptions
    }),
    always({
      title: getMiddleText(
        'NIVEAUXGARANTIESETTARIFS_GAR_DECES_DECES_PTIA_ACC_LABEL'
      ),
      price: concat(
        toString(propOr(0.0, 'annualPrice', OPTION_ACC)),
        ' € / an'
      ),
      options: PTIADeathOptions
    })
  )(
    compose(
      prop('isSelected'),
      find(propEq('code', 'OPTION_TT_CAUSE')),
      defaultTo([])
    )(guarantees)
  );

  // SpousePensionDeathPrevTns
  const spousePensionDeathOptions = [
    {
      displayed: true,
      label: getMiddleText(
        'NIVEAUXGARANTIESETTARIFS_GAR_DECES_RCONJ_CAPITAL_LABEL'
      ),
      value: getPropForCode('label', 'RCONJ_CAP')(guarantees),
      classNameLabel: 'font-bold',
      classNameValue: 'text-right'
    },
    {
      displayed: true,
      label: (
        <div className={'flex flex-col gap-1'}>
          <div className={'font-bold'}>Conjoint</div>
          <div>
            Né le{' '}
            {compose(
              dateTsToFr,
              prop('birthDate'),
              find(propEq('type', 'SPOUSE'))
            )(grantees)}
          </div>
        </div>
      )
    },
    {
      displayed: true,
      label: getMiddleText(
        'NIVEAUXGARANTIESETTARIFS_GAR_DECES_RCONJ_FISCA_MADELIN_LABEL'
      ),
      value: getIsSelected('RCONJ_MAD', guarantees) ? 'Oui' : 'Non',
      classNameLabel: 'font-bold',
      classNameValue: 'text-right'
    }
  ];

  const spousePensionGuarantees = {
    title: getMiddleText('NIVEAUXGARANTIESETTARIFS_GAR_DECES_RCONJ_LABEL'),
    price: concat(toString(propOr(0.0, 'annualPrice', RCONJ_CAP)), ' € / an'),
    options: spousePensionDeathOptions
  };

  // EducationPensionDeathPrevTns
  const educationPensionDeathOptions = [
    {
      displayed: true,
      label: getMiddleText(
        'NIVEAUXGARANTIESETTARIFS_GAR_DECES_REDUC_CAPITAL_LABEL'
      ),
      classNameLabel: 'font-bold'
    },
    {
      displayed: true,
      label: (
        <div>
          {map((leg) => <div key={leg}>{leg}</div>)(
            formatLegendAndLabel('legend', 'REDUC_CAP', guarantees)
          )}
        </div>
      ),
      value: (
        <div>
          {map((val) => <div key={val}>{val}</div>)(
            formatLegendAndLabel('label', 'REDUC_CAP', guarantees)
          )}
        </div>
      ),
      classNameValue: 'text-right'
    },
    {
      displayed: true,
      label: (
        <div>
          <div className={'font-bold'}>Enfants</div>
          {map((child) => (
            <div className={'font-thin'} key={child.birthDate}>
              Né le {compose(dateTsToFr, prop('birthDate'))(child)}
            </div>
          ))(filter(propEq('type', 'CHILD'))(grantees))}
        </div>
      ),
      classNameLabel: 'font-bold'
    },
    {
      displayed: true,
      label: getMiddleText(
        'NIVEAUXGARANTIESETTARIFS_GAR_DECES_REDUC_FISCA_MADELIN_LABEL'
      ),
      value: getIsSelected('REDUC_MAD', guarantees) ? 'Oui' : 'Non',
      classNameLabel: 'font-bold',
      classNameValue: 'text-right'
    }
  ];

  const educationPensionGuarantees = {
    title: getMiddleText('NIVEAUXGARANTIESETTARIFS_GAR_DECES_REDUC_LABEL'),
    price: concat(toString(propOr(0.0, 'annualPrice', REDUC_CAP)), ' € / an'),
    options: educationPensionDeathOptions
  };

  // INCAPACITE
  const disabilityOptions = [
    {
      displayed:
        getMiddleBoolean(
          'NIVEAUXGARANTIESETTARIFS_GAR_INCAP_FRANCHISE_TTES_CAUSES_EST_NECESSAIRE'
        ) ||
        getMiddleBoolean(
          'NIVEAUXGARANTIESETTARIFS_GAR_INCAP_FRANCHISE_MALADIE_EST_NECESSAIRE'
        ) ||
        getMiddleBoolean(
          'NIVEAUXGARANTIESETTARIFS_GAR_INCAP_FRANCHISE_HOSPITALISATION_EST_NECESSAIRE'
        ) ||
        getMiddleBoolean(
          'NIVEAUXGARANTIESETTARIFS_GAR_INCAP_FRANCHISE_ACCIDENT_EST_NECESSAIRE'
        ),
      label: 'Franchise',
      classNameLabel: 'font-bold'
    },
    {
      displayed: getMiddleBoolean(
        'NIVEAUXGARANTIESETTARIFS_GAR_INCAP_FRANCHISE_MALADIE_EST_NECESSAIRE'
      ),
      label: 'En cas de maladie',
      value: getPropForCode('label', 'OPTION_FR_MAL')(guarantees),
      classNameValue: 'text-right'
    },
    {
      displayed: getMiddleBoolean(
        'NIVEAUXGARANTIESETTARIFS_GAR_INCAP_FRANCHISE_ACCIDENT_EST_NECESSAIRE'
      ),
      label: 'En cas d\'accident',
      value: getPropForCode('label', 'OPTION_FR_ACC')(guarantees),
      classNameValue: 'text-right'
    },
    {
      displayed: getMiddleBoolean(
        'NIVEAUXGARANTIESETTARIFS_GAR_INCAP_FRANCHISE_HOSPITALISATION_EST_NECESSAIRE'
      ),
      label: 'En cas d\'hospitalisation',
      value: getPropForCode('label', 'OPTION_FR_HOSP')(guarantees),
      classNameValue: 'text-right'
    },
    {
      displayed: getMiddleBoolean(
        'NIVEAUXGARANTIESETTARIFS_GAR_INCAP_FRANCHISE_TTES_CAUSES_EST_NECESSAIRE'
      ),
      label: 'Toutes causes',
      value: getPropForCode('label', 'OPTION_FR_ALL')(guarantees),
      classNameValue: 'text-right'
    },
    {
      displayed: true,
      label: 'Montant revenu garanti',
      value: getPropForCode('label', 'OPTION_INCAP_CAP')(guarantees),
      classNameLabel: 'font-bold',
      classNameValue: 'text-right font-bold'
    },
    {
      displayed: true,
      label: getMiddleText(
        'NIVEAUXGARANTIESETTARIFS_GAR_INCAP_FISCA_MADELIN_LABEL'
      ),
      value: getIsSelected('INCAP_MAD', guarantees) ? 'Oui' : 'Non',
      classNameValue: 'text-right'
    },
    {
      displayed:
        getMiddleBoolean(
          'NIVEAUXGARANTIESETTARIFS_GAR_INCAP_OPTION_EST_NECESSAIRE'
        ) &&
        (getIsSelected('OPTION_ZERO_SIN', guarantees) ||
          getIsSelected('OPTION_EXO_COT', guarantees) ||
          getIsSelected('OPTION_CONFORT_PSY', guarantees) ||
          getIsSelected('OPTION_HOSP_ENF_GAR', guarantees)),
      label: getMiddleText('NIVEAUXGARANTIESETTARIFS_GAR_INCAP_OPTION_LABEL'),
      classNameLabel: 'font-bold'
    },
    {
      displayed:
        getMiddleBoolean(
          'NIVEAUXGARANTIESETTARIFS_GAR_INCAP_OPTION_ZERO_SINISTRE_EST_NECESSAIRE'
        ) && getIsSelected('OPTION_ZERO_SIN', guarantees),
      label: getMiddleText(
        'NIVEAUXGARANTIESETTARIFS_GAR_INCAP_OPTION_ZERO_SINISTRE_LABEL'
      ),
      value: concat(
        toString(propOr(0.0, 'annualPrice', OPTION_ZERO_SIN)),
        ' €'
      ),
      classNameValue: 'text-right'
    },
    {
      displayed:
        getMiddleBoolean(
          'NIVEAUXGARANTIESETTARIFS_GAR_INCAP_OPTION_EXO_COTISATION_EST_NECESSAIRE'
        ) && getIsSelected('OPTION_EXO_COT', guarantees),
      label: getMiddleText(
        'NIVEAUXGARANTIESETTARIFS_GAR_INCAP_OPTION_EXO_COTISATION_LABEL'
      ),
      value: concat(toString(propOr(0.0, 'annualPrice', OPTION_EXO_COT)), ' €'),
      classNameValue: 'text-right'
    },
    {
      displayed:
        getMiddleBoolean(
          'NIVEAUXGARANTIESETTARIFS_GAR_INCAP_OPTION_CONFORT_PSY_EST_NECESSAIRE'
        ) && getIsSelected('OPTION_CONFORT_PSY', guarantees),
      label: getMiddleText(
        'NIVEAUXGARANTIESETTARIFS_GAR_INCAP_OPTION_CONFORT_PSY_LABEL'
      ),
      value: concat(
        toString(propOr(0.0, 'annualPrice', OPTION_CONFORT_PSY)),
        ' €'
      ),
      classNameValue: 'text-right'
    },
    {
      displayed:
        getMiddleBoolean(
          'NIVEAUXGARANTIESETTARIFS_GAR_INCAP_OPTION_HOSPI_CHILD_EST_NECESSAIRE'
        ) && getIsSelected('OPTION_HOSP_ENF_GAR', guarantees),
      label: getMiddleText(
        'NIVEAUXGARANTIESETTARIFS_GAR_INCAP_OPTION_HOSPI_CHILD_LABEL'
      ),
      value: concat(
        toString(propOr(0.0, 'annualPrice', HOSP_ENF_ALLOW_AMOUNT)),
        ' €'
      ),
      classNameValue: 'text-right'
    },
    {
      displayed:
        getMiddleBoolean(
          'NIVEAUXGARANTIESETTARIFS_GAR_INCAP_OPTION_HOSPI_CHILD_EST_NECESSAIRE'
        ) && getIsSelected('OPTION_HOSP_ENF_GAR', guarantees),
      label: (
        <div>
          <div className={'font-bold'}>Enfants</div>
          {map((child) => (
            <div className={'font-thin'} key={child.birthDate}>
              Né le {compose(dateTsToFr, prop('birthDate'))(child)}
            </div>
          ))(filter(propEq('type', 'CHILD'))(grantees))}
        </div>
      ),
      classNameLabel: 'font-bold'
    },
    {
      displayed:
        getMiddleBoolean(
          'NIVEAUXGARANTIESETTARIFS_GAR_INCAP_OPTION_HOSPI_CHILD_EST_NECESSAIRE'
        ) && getIsSelected('OPTION_HOSP_ENF_GAR', guarantees),
      label: getMiddleText(
        'NIVEAUXGARANTIESETTARIFS_GAR_INCAP_OPTION_HOSPI_CHILD_MT_IJ_LABEL'
      ),
      value: getPropForCode('label', 'HOSP_ENF_ALLOW_AMOUNT')(guarantees),
      classNameLabel: 'font-bold',
      classNameValue: 'text-right font-bold'
    },
    {
      displayed:
        getMiddleBoolean(
          'NIVEAUXGARANTIESETTARIFS_GAR_INCAP_OPTION_HOSPI_CHILD_EST_NECESSAIRE'
        ) && getIsSelected('OPTION_HOSP_ENF_GAR', guarantees),
      label: getMiddleText(
        'NIVEAUXGARANTIESETTARIFS_GAR_INCAP_OPTION_HOSPI_CHILD_FISCA_MADELIN_LABEL'
      ),
      value: getIsSelected('HOSP_ENF_MAD', guarantees) ? 'Oui' : 'Non',
      classNameLabel: 'font-bold',
      classNameValue: 'text-right'
    },
    {
      displayed:
        getMiddleBoolean(
          'NIVEAUXGARANTIESETTARIFS_GAR_INCAP_OPTION_FRAIS_GENERAUX_EST_NECESSAIRE'
        ) && getIsSelected('OPTION_FR_GEN', guarantees),
      label: getMiddleText(
        'NIVEAUXGARANTIESETTARIFS_GAR_INCAP_OPTION_FRAIS_GENERAUX_LABEL'
      ),
      value: concat(toString(propOr(0.0, 'annualPrice', OPTION_FR_GEN)), ' €'),
      classNameLabel: 'font-bold',
      classNameValue: 'text-right font-bold'
    },
    {
      displayed:
        (getMiddleBoolean(
          'NIVEAUXGARANTIESETTARIFS_GAR_INCAP_OPTION_FRAIS_GENERAUX_FRANCHISE_TTES_CAUSES_EST_NECESSAIRE'
        ) ||
          getMiddleBoolean(
            'NIVEAUXGARANTIESETTARIFS_GAR_INCAP_OPTION_FRAIS_GENERAUX_FRANCHISE_MALADIE_EST_NECESSAIRE'
          ) ||
          getMiddleBoolean(
            'NIVEAUXGARANTIESETTARIFS_GAR_INCAP_OPTION_FRAIS_GENERAUX_FRANCHISE_HOSPITALISATION_EST_NECESSAIRE'
          ) ||
          getMiddleBoolean(
            'NIVEAUXGARANTIESETTARIFS_GAR_INCAP_OPTION_FRAIS_GENERAUX_FRANCHISE_ACCIDENT_EST_NECESSAIRE'
          )) &&
        getIsSelected('OPTION_FR_GEN', guarantees),
      label: 'Franchise',
      classNameLabel: 'font-bold'
    },
    {
      displayed:
        getMiddleBoolean(
          'NIVEAUXGARANTIESETTARIFS_GAR_INCAP_OPTION_FRAIS_GENERAUX_FRANCHISE_MALADIE_EST_NECESSAIRE'
        ) && getIsSelected('OPTION_FR_GEN', guarantees),
      label: 'En cas de maladie',
      value: getPropForCode('label', 'OPTION_FR_GEN_FR_MAL')(guarantees),
      classNameValue: 'text-right'
    },
    {
      displayed:
        getMiddleBoolean(
          'NIVEAUXGARANTIESETTARIFS_GAR_INCAP_OPTION_FRAIS_GENERAUX_FRANCHISE_ACCIDENT_EST_NECESSAIRE'
        ) && getIsSelected('OPTION_FR_GEN', guarantees),
      label: 'En cas d\'accident',
      value: getPropForCode('label', 'OPTION_FR_GEN_FR_ACC')(guarantees),
      classNameValue: 'text-right'
    },
    {
      displayed:
        getMiddleBoolean(
          'NIVEAUXGARANTIESETTARIFS_GAR_INCAP_OPTION_FRAIS_GENERAUX_FRANCHISE_HOSPITALISATION_EST_NECESSAIRE'
        ) && getIsSelected('OPTION_FR_GEN', guarantees),
      label: 'En cas d\'hospitalisation',
      value: getPropForCode('label', 'OPTION_FR_GEN_FR_HOSP')(guarantees),
      classNameValue: 'text-right'
    },
    {
      displayed:
        getMiddleBoolean(
          'NIVEAUXGARANTIESETTARIFS_GAR_INCAP_OPTION_FRAIS_GENERAUX_FRANCHISE_TTES_CAUSES_EST_NECESSAIRE'
        ) && getIsSelected('OPTION_FR_GEN', guarantees),
      label: 'Toutes causes',
      value: getPropForCode('label', 'OPTION_FR_GEN_FR_TT_CAUSE')(guarantees),
      classNameValue: 'text-right'
    },
    {
      displayed: getIsSelected('OPTION_FR_GEN', guarantees),
      label: 'Montant revenu garanti',
      value: getPropForCode('label', 'FR_GEN_CAP')(guarantees),
      classNameLabel: 'font-bold',
      classNameValue: 'text-right font-bold'
    }
  ];

  const disabilityGuarantees = {
    title: getMiddleText('NIVEAUXGARANTIESETTARIFS_GAR_INCAP_LABEL'),
    price: concat(
      toString(propOr(0.0, 'annualPrice', OPTION_INCAP_CAP)),
      ' € / an'
    ),
    options: disabilityOptions
  };

  // INVALIDITE
  const invalidityOptions = [
    {
      displayed: true,
      label: (
        <div>
          {map((leg) => <div key={leg}>{leg}</div>)(
            formatLegendAndLabel('legend', 'INVAL_CAP', guarantees)
          )}
        </div>
      ),
      value: (
        <div>
          {map((val) => <div key={val}>{val}</div>)(
            formatLegendAndLabel('label', 'INVAL_CAP', guarantees)
          )}
        </div>
      ),
      classNameValue: 'text-right'
    },
    {
      displayed: true,
      label: getMiddleText(
        'NIVEAUXGARANTIESETTARIFS_GAR_INVAL_FISCA_MADELIN_LABEL'
      ),
      value: getIsSelected('INVAL_MAD', guarantees) ? 'Oui' : 'Non',
      classNameLabel: 'font-bold',
      classNameValue: 'text-right'
    },
    {
      displayed:
        getMiddleBoolean(
          'NIVEAUXGARANTIESETTARIFS_GAR_INVAL_OPTION_EST_NECESSAIRE'
        ) && getIsSelected('OPTION_INVAL_DEP', guarantees),
      label: getMiddleText('NIVEAUXGARANTIESETTARIFS_GAR_INVAL_OPTION_LABEL'),
      classNameLabel: 'font-bold'
    },
    {
      displayed:
        getMiddleBoolean(
          'NIVEAUXGARANTIESETTARIFS_GAR_INVAL_OPTION_DEPENDANCE_EST_NECESSAIRE'
        ) && getIsSelected('OPTION_INVAL_DEP', guarantees),
      label: getMiddleText(
        'NIVEAUXGARANTIESETTARIFS_GAR_INVAL_OPTION_DEPENDANCE_LABEL'
      ),
      value: concat(toString(propOr(0.0, 'annualPrice', INVAL_DEP_CAP)), ' €'),
      classNameValue: 'text-right'
    },
    {
      displayed:
        getMiddleBoolean(
          'NIVEAUXGARANTIESETTARIFS_GAR_INVAL_OPTION_DEPENDANCE_EST_NECESSAIRE'
        ) && getIsSelected('OPTION_INVAL_DEP', guarantees),
      label: getMiddleText(
        'NIVEAUXGARANTIESETTARIFS_GAR_INVAL_OPTION_DEPENDANCE_CAPITAL_LABEL'
      ),
      value: concat(getValue('INVAL_DEP_CAP', guarantees) || '-', ' €'),
      classNameValue: 'text-right'
    }
  ];

  const invalidityGuarantees = {
    title: getMiddleText('NIVEAUXGARANTIESETTARIFS_GAR_INVAL_LABEL'),
    price: concat(
      toString(propOr(0.0, 'annualPrice', OPTION_INVAL)),
      ' € / an'
    ),
    options: invalidityOptions
  };

  const taskData = {
    taskName: 'sendForSignature',
    projectId: context?.projectId,
    metadata: {
      step: signatureStepEnum.ADVICE
    }
  };

  useSubscription(TaskSubscription, {
    variables: taskData,
    onData: ({ data }) => {
      const status = pathOr(
        {},
        ['data', 'taskSubscription', 'state', 'status'],
        data
      );
      if (status === 'ERROR') {
        setProjectStatus('ADVICE_SIGNATURE_REFUSED');
      } else if (status === 'DONE') {
        setSendingSignature(false);
        setProjectStatus('ADVICE_SENT_FOR_SIGNATURE');
      } else if (status === 'SIGNED') {
        setProjectStatus('ADVICE_SIGNED');
      }
    }
  });

  const saveCustomerInfo = () => {
    const customer = objOf(
      'customer',
      pick(['emails', 'phones'], formValues.customer)
    );

    saveCustomerInfoMutation(customer, (ok, error) => {
      if (ok && !error) {
        setModalConfirmed(true);
        setDisplayConfirmationModal(false);
        onSubmit();
      }
    });
  };

  const beneficiariesEnrollment = propOr(
    false,
    'beneficiariesEnrollment',
    project
  );

  const displayCommissionBlock =
    getMiddleBoolean(
      'NIVEAUXGARANTIESETTARIFS_TAUX_COMMISSION_EST_NECESSAIRE'
    ) &&
    offerCode === offerCodeEnum.SELF_EMPLOYED_WORKER_PROTECTION &&
    includes('BROKER', usersInformation[0].roles);

  return {
    form,
    onSubmit,
    onModifyEffectDate,
    onEditCustomerGrantees,
    onEditCompany,
    onEditDemography,
    downloadMutation,
    sendEmailMutation,
    $onClickPrev,
    documentsDownloaded,
    setDocumentsDownloaded,
    monthlyPrice: prop('monthlyPrice', calculatedPrices),
    annualPrice: prop('annualPrice', calculatedPrices),
    monthlyPriceMadelin: prop('monthlyPriceMadelin', calculatedPrices),
    annualPriceMadelin: prop('annualPriceMadelin', calculatedPrices),
    pricingLevel: prop('pricingLevel', calculatedPrices),
    isDutyOfAdviceSkipped,
    offerCode,
    canContinueToSubscription,
    guarantees,
    PTIADeathGuarantees,
    spousePensionGuarantees,
    educationPensionGuarantees,
    disabilityGuarantees,
    invalidityGuarantees,
    subscriptionStatus,
    numberPricings,
    signatureAdvice,
    isAdviceSignatureSentForSignature,
    isAdviceSignatureError,
    isAdviceSignatureSigned,
    displayConfirmationModal,
    setDisplayConfirmationModal,
    saveCustomerInfo,
    sendingSignature,
    cantGoNext,
    packs,
    isViewModeCustomer,
    currentIndex,
    setCurrentIndex,
    beneficiariesEnrollment,
    displayCommissionBlock,
    canSendMail,
    setCanSendMail
  };
};

export default useSimulationRecap;
